import { ManifestPackageStatus, SsccStatus } from '../common/status';
import dayjs from 'dayjs';
import { ReceiptEventPackageItemProps, SSCCProps } from '../datastore/ssccs';
import { ManifestPackageInvLineItemProps, ManifestPackageProps } from '../datastore/tripSheets';
import { Manifest } from '../pages/admin-panel/utils/types';

export const updateSsccTimeBasedOnStatus = ({
  status,
  sscc,
  weight
}: {
  status: SsccStatus;
  sscc: SSCCProps;
  weight?: string;
}): SSCCProps => {
  const now = dayjs();

  switch (status) {
    case SsccStatus.open:
      return {
        ...sscc,
        status,
        ...(weight ? { weight } : {}),
        open: sscc.open ?? now
      };

    case SsccStatus.staged:
      return {
        ...sscc,
        status,
        ...(weight ? { weight } : {}),
        staged: sscc.staged ?? now
      };

    case SsccStatus.manifested:
      return {
        ...sscc,
        status,
        ...(weight ? { weight } : {}),
        staged: sscc.staged ?? now,
        manifested: sscc.manifested ?? now
      };

    case SsccStatus.inTransit:
      return {
        ...sscc,
        status,
        ...(weight ? { weight } : {}),
        staged: sscc.staged ?? now,
        manifested: sscc.manifested ?? now,
        inTransit: sscc.inTransit ?? now
      };

    case SsccStatus.approaching:
      return {
        ...sscc,
        status,
        ...(weight ? { weight } : {}),
        staged: sscc.staged ?? now,
        manifested: sscc.manifested ?? now,
        inTransit: sscc.inTransit ?? now,
        approaching: sscc.approaching ?? now
      };

    case SsccStatus.delivered:
      return {
        ...sscc,
        status,
        ...(weight ? { weight } : {}),
        staged: sscc.staged ?? now,
        manifested: sscc.manifested ?? now,
        inTransit: sscc.inTransit ?? now,
        approaching: sscc.approaching ?? now,
        delivered: sscc.delivered ?? now
      };

    default:
      return { ...sscc, status };
  }
};

export const mapInventoryLines = (
  lines: ReceiptEventPackageItemProps[] | undefined
): ManifestPackageInvLineItemProps[] => {
  return (lines ?? []).filter(Boolean).map((item) => {
    return {
      orderLineId: item?.orderLineId!,
      partId: item?.partId!,
      sourcePartId: item?.sourcePartId!,
      description: item?.description!,
      quantityReceived: item?.quantityReceived!,
      purchaseType: item?.purchaseType!,
      receivingWarehouseId: item?.receivingWarehouseId!,
      receivingWarehouseDefaultLocationId: item?.receivingWarehouseDefaultLocationId!,
      inTransitWarehouseId: item?.inTransitWarehouseId!,
      inTransitWarehouseLocationId: item?.inTransitWarehouseLocationId!
    };
  });
};

export const flatMapPackagesAndAsnLines = ({
  originalSSCC,
  inventoryTransfer
}: {
  originalSSCC: Partial<SSCCProps>;
  inventoryTransfer: boolean;
}): ManifestPackageProps[] => {
  const result =
    originalSSCC.receiptedEvents?.flatMap((receiptedEvent) => {
      return receiptedEvent.packages.map((localPackage) => {
        return {
          id: crypto.randomUUID(),
          maxQuantity: localPackage.quantity || 0,
          quantity: inventoryTransfer ? localPackage.quantity || 0 : 0,
          receivedStatus: inventoryTransfer ? ManifestPackageStatus.received : ManifestPackageStatus.notReceived,
          asn: localPackage.asn || '',
          asnExists: true,
          poNumber: localPackage.po || '',
          packageType: localPackage.packageType?.toUpperCase() || '',
          height: originalSSCC.height || '',
          width: originalSSCC.width || '',
          depth: originalSSCC.depth || '',
          inventoryLines: mapInventoryLines(localPackage.items)
        };
      });
    }) || [];

  return result;
};

export const extractASNsFromManifest = (manifest: Manifest): string[] => {
  const asnSet = new Set<string>();

  (manifest.ssccs || []).forEach((sscc: any) => {
    (sscc?.packages || []).forEach((pkg: any) => {
      if (pkg?.asn && pkg.receivedStatus === ManifestPackageStatus.notReceived) {
        asnSet.add(pkg.asn);
      }
    });
  });

  return Array.from(asnSet);
};
